
import React, { useState, Suspense } from "react"
import { Plus } from "lucide-react"
import { useParams, useNavigate } from "react-router-dom"
import CostCodeBudgetTable from "../components/CostCodeBudgetTable"
import ProjectFormDialog from "../components/ProjectFormDialog"
import ProjectMembersTab from "../components/ProjectMembersTab"
import type { CostCodeBudget } from '../hooks/types'
import SeatAssignmentDrawer from "../components/SeatAssignmentDrawer"
import {
  useProject,
  useUpdateProject,
  useUpdateCostCodeBudgets,
  useRemoveSeatFromProject,
  useAttachSeatToProject,
} from "../hooks/useProjects"
import { useProjectMeta } from "../hooks/useProjectMeta"
import { useSeats } from "../hooks/useSeats"
import { useAvailablePersonnel } from "../hooks/usePersonnel"
// import CrewTab from "../components/CrewTab"  // removed

export default function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState("overview")

  // State for seat assignment drawer
  const [isSeatDrawerOpen, setIsSeatDrawerOpen] = useState(false)
  const [selectedRole, setSelectedRole] = useState<"Project Manager" | "Foreman">("Project Manager")

  // Queries and mutations
  const projectQuery = useProject(id || "")
  const updateCostCodeBudgets = useUpdateCostCodeBudgets(id || "")
  const removeSeat = useRemoveSeatFromProject(id || "")
  const attachSeat = useAttachSeatToProject(id || "")

  // Fetch available seats and personnel
  const availableSeatsQuery = useSeats({ role: selectedRole, status: "active", projectId: "null" })
  const availablePersonnelQuery = useAvailablePersonnel(selectedRole)
  // Edit project hook and state
  const updateProject = useUpdateProject(id || "")
  const [isEditOpen, setIsEditOpen] = useState(false)
  const projectMetaQuery = useProjectMeta()
  // Derived default values for edit form
  const projectData = projectQuery.data
  const defaultValues = projectData
    ? {
        projectCode: projectData.projectCode,
        title: projectData.title,
        clientId: projectData.clientId._id,
        location: projectData.location,
        status: projectData.status,
        bidDate: projectData.bidDate || undefined,
        awardDate: projectData.awardDate || undefined,
        fabricationStartDate: projectData.fabricationStartDate || undefined,
        fabricationEndDate: projectData.fabricationEndDate || undefined,
        erectionStartDate: projectData.erectionStartDate || undefined,
        erectionEndDate: projectData.erectionEndDate || undefined,
        completionDate: projectData.completionDate || undefined,
        projectManagerPersonnelId:
          projectData.seatAssignments.find(sa => sa.role === 'Project Manager' && !sa.removed)?.seatId.userId?._id || '',
        foremanPersonnelIds: projectData.seatAssignments
          .filter(sa => sa.role === 'Foreman' && !sa.removed)
          .map(sa => sa.seatId.userId?._id)
          .filter((id): id is string => Boolean(id)),
        budget: {
          hours: projectData.budget.hours,
          labourRate: projectData.budget.labourRate,
          currency: projectData.budget.currency,
        },
      }
    : undefined

  // Handle seat removal
  const handleRemoveSeat = async (seatId: string) => {
    try {
      await removeSeat.mutateAsync(seatId)
    } catch (error) {
      console.error("Failed to remove seat:", error)
    }
  }

  // Handle seat assignment
  const handleAssignSeat = async (seatId: string, personnelId: string) => {
    try {
      await attachSeat.mutateAsync({ seatId, personnelId })
      setIsSeatDrawerOpen(false)
    } catch (error) {
      console.error("Failed to assign seat:", error)
    }
  }

  // Format date for display
  const formatDate = (dateString?: string) => {
    if (!dateString) return "Not set"
    return new Date(dateString).toLocaleDateString()
  }

  // Get status badge color
  const getStatusColor = (status: string) => {
    switch (status) {
      case "Planning":
        return "bg-blue-100 text-blue-800"
      case "Bidding":
        return "bg-purple-100 text-purple-800"
      case "Awarded":
        return "bg-green-100 text-green-800"
      case "Fabrication":
        return "bg-amber-100 text-amber-800"
      case "Erection":
        return "bg-orange-100 text-orange-800"
      case "Completed":
        return "bg-gray-100 text-gray-800"
      case "OnHold":
        return "bg-yellow-100 text-yellow-800"
      case "Cancelled":
        return "bg-red-100 text-red-800"
      default:
        return "bg-gray-100 text-gray-800"
    }
  }

  if (projectQuery.isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <svg className="h-8 w-8 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      </div>
    )
  }

  if (projectQuery.isError || !projectData) {
    return (
      <div className="container mx-auto py-8 px-4">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          Failed to load project details. Please try again.
        </div>
        <button
          className="mt-4 flex items-center text-blue-600 hover:text-blue-800"
          onClick={() => navigate("/myteconstruction/dashboard/projects")}
        >
          <svg
            className="w-4 h-4 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Projects
        </button>
      </div>
    )
  }

  const project = projectData

  return (
    <div className="container mx-auto py-8 px-4">
      <button
        className="mb-4 flex items-center text-blue-600 hover:text-blue-800"
        onClick={() => navigate("/myteconstruction/dashboard/projects")}
      >
        <svg
          className="w-4 h-4 mr-2"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        Back to Projects
      </button>

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold">{project.title}</h1>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-gray-500">Code: {project.projectCode}</span>
            <span className="text-gray-500">•</span>
            <span
              className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(project.status)}`}
            >
              {project.status}
            </span>
          </div>
        </div>
        <button
          onClick={() => setIsEditOpen(true)}
          className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 flex items-center"
        >
          <Plus className="w-4 h-4 mr-2" />
          Edit Project
        </button>
      </div>
      {/* Edit Project Dialog */}
      {isEditOpen && (
        <Suspense fallback={null}>
      <ProjectFormDialog
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        onSubmit={async (data) => {
          await updateProject.mutateAsync(data)
          setIsEditOpen(false)
          projectQuery.refetch()
        }}
        isLoading={updateProject.isPending}
            projectMeta={projectMetaQuery.data}
            isMetaLoading={projectMetaQuery.isLoading}
            defaultValues={defaultValues}
            mode="edit"
            project={project}
          />
        </Suspense>
      )}

      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab("overview")}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === "overview"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab("seats")}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === "seats"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Seats
            </button>
            <button
              onClick={() => setActiveTab("cost-codes")}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === "cost-codes"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Cost Codes
            </button>
            <button
              onClick={() => setActiveTab("members")}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === "members"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Members
            </button>
            <button
              onClick={() => setActiveTab("timeline")}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === "timeline"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Timeline
            </button>
          </nav>
        </div>
      </div>

      {/* Overview Tab */}
      {activeTab === "overview" && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
                <svg
                  className="w-5 h-5 mr-2 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                Project Dates
              </h3>
            </div>
            <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
              <dl className="sm:divide-y sm:divide-gray-200">
                <div className="py-3 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Bid Date</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{formatDate(project.bidDate)}</dd>
                </div>
                <div className="py-3 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Award Date</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{formatDate(project.awardDate)}</dd>
                </div>
                <div className="py-3 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Fabrication</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {formatDate(project.fabricationStartDate)} - {formatDate(project.fabricationEndDate)}
                  </dd>
                </div>
                <div className="py-3 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Erection</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {formatDate(project.erectionStartDate)} - {formatDate(project.erectionEndDate)}
                  </dd>
                </div>
                <div className="py-3 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Completion</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {formatDate(project.completionDate)}
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
                <svg
                  className="w-5 h-5 mr-2 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                Budget
              </h3>
            </div>
            <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
              <dl className="sm:divide-y sm:divide-gray-200">
                <div className="py-3 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Total Hours</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {project.totalBudgetHours.toLocaleString()}
                  </dd>
                </div>
                <div className="py-3 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Labour Rate</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {project.budget.labourRate.toLocaleString(undefined, {
                      style: "currency",
                      currency: project.budget.currency,
                    })}
                  </dd>
                </div>
                <div className="py-3 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Total Budget</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {project.budget.amount.toLocaleString(undefined, {
                      style: "currency",
                      currency: project.budget.currency,
                    })}
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
                <svg
                  className="w-5 h-5 mr-2 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
                Client
              </h3>
            </div>
            <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
              <dl className="sm:divide-y sm:divide-gray-200">
                <div className="py-3 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Name</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{project.clientId.name}</dd>
                </div>
                <div className="py-3 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Email</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{project.clientId.email}</dd>
                </div>
                <div className="py-3 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Location</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {project.location || "Not specified"}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      )}

      {/* Seats Tab */}
      {activeTab === "seats" && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium">Assigned Seats</h3>
            <div className="flex space-x-2">
              <button
                onClick={() => {
                  setSelectedRole("Project Manager")
                  setIsSeatDrawerOpen(true)
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center"
              >
                <Plus className="w-4 h-4 mr-2" />
                Assign PM Seat
              </button>
              <button
                onClick={() => {
                  setSelectedRole("Foreman")
                  setIsSeatDrawerOpen(true)
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center"
              >
                <Plus className="w-4 h-4 mr-2" />
                Assign Foreman Seat
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {project.seatAssignments
              .filter((assignment) => !assignment.removed)
              .map((assignment) => (
                  <div key={assignment.seatId._id} className="bg-white shadow rounded-lg overflow-hidden">
                  <div className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="text-base font-semibold text-gray-900">{assignment.role}</h4>
                        <p className="text-sm text-gray-700">{assignment.seatId.userId?.name || "Unassigned"}</p>
                        <p className="text-xs text-gray-500">
                          Assigned: {new Date(assignment.assignedAt).toLocaleDateString()}
                        </p>
                      </div>
                      <button
                        onClick={() => handleRemoveSeat(assignment.seatId._id)}
                        disabled={removeSeat.isPending}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        {removeSeat.isPending ? (
                          <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                        ) : (
                          "Remove"
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}

            {project.seatAssignments.filter((a) => !a.removed).length === 0 && (
              <div className="col-span-full text-center py-8 bg-gray-50 rounded-lg border border-dashed">
                <p className="text-gray-500">No seats assigned to this project</p>
                <button
                  onClick={() => {
                    setSelectedRole("Project Manager")
                    setIsSeatDrawerOpen(true)
                  }}
                  className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 inline-flex items-center"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Assign Seat
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Cost Codes Tab */}
      {activeTab === "cost-codes" && (
        <CostCodeBudgetTable
          costCodeBudgets={project.costCodeBudgets}
          projectBudgetHours={project.budget.hours}
          onSave={(budgets: CostCodeBudget[]) => updateCostCodeBudgets.mutate(budgets)}
          isLoading={updateCostCodeBudgets.isPending}
        />
      )}
      {/* Project Members Tab (Org-only) */}
      {activeTab === "members" && (
        <div className="pt-6">
          <ProjectMembersTab projectId={id || ""} />
        </div>
      )}
      {/* Crew Management (removed) */}

      {/* Timeline Tab */}
      {activeTab === "timeline" && (
        <div className="bg-gray-50 p-8 rounded-lg border text-center">
          <h3 className="text-lg font-medium text-gray-500">Timeline View</h3>
          <p className="text-gray-400 mt-2">Coming soon</p>
        </div>
      )}

      {/* Seat Assignment Drawer */}
        <SeatAssignmentDrawer
          isOpen={isSeatDrawerOpen}
          onClose={() => setIsSeatDrawerOpen(false)}
          onAssign={handleAssignSeat}
          availableSeats={availableSeatsQuery.data || []}
          availablePersonnel={availablePersonnelQuery.data || []}
          isLoading={attachSeat.isPending}
          role={selectedRole}
        />
    </div>
  )
}
